import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ChevronDown, X } from 'lucide-react';
import clsx from 'clsx';
import { TELTONIKA_COMMANDS, TELTONIKA_COMMAND_CATEGORIES, type TeltonikaCommand } from '../data/teltonika-commands';

interface Props {
  value: string;
  onChange: (command: string, cmd?: TeltonikaCommand | null) => void;
}

const DEVICE_BADGE_COLORS: Record<string, string> = {
  'Tous': 'bg-green-100 text-green-700',
  'FMC003': 'bg-blue-100 text-blue-700',
  'FMB020': 'bg-purple-100 text-purple-700',
  'FMT100': 'bg-orange-100 text-orange-700',
  'FMC003, FMT100': 'bg-teal-100 text-teal-700',
  'FMB020, FMT100': 'bg-pink-100 text-pink-700',
};

export default function TeltonikaCommandPicker({ value, onChange }: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filter commands
  const filtered = useMemo(() => {
    if (!search) return TELTONIKA_COMMANDS;
    const q = search.toLowerCase();
    return TELTONIKA_COMMANDS.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.description.toLowerCase().includes(q) ||
        cmd.value.toLowerCase().includes(q) ||
        cmd.category.toLowerCase().includes(q) ||
        cmd.devices.toLowerCase().includes(q)
    );
  }, [search]);

  // Group by category
  const grouped = useMemo(() => {
    const groups: Record<string, TeltonikaCommand[]> = {};
    for (const cat of TELTONIKA_COMMAND_CATEGORIES) {
      const cmds = filtered.filter((c) => c.category === cat);
      if (cmds.length > 0) groups[cat] = cmds;
    }
    return groups;
  }, [filtered]);

  const selectedCmd = TELTONIKA_COMMANDS.find((c) => c.value === value);

  const handleSelect = (cmd: TeltonikaCommand) => {
    onChange(cmd.value, cmd);
    setIsOpen(false);
    setSearch('');
  };

  const handleCustom = () => {
    onChange('custom', null);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange('', null);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{t('teltonika_picker.label')}</label>

      {/* Trigger */}
      <div
        className={clsx(
          'flex items-center gap-2 w-full px-3 py-2 bg-white border rounded-lg cursor-pointer transition-colors',
          isOpen ? 'border-cyan-500 ring-2 ring-cyan-500/20' : 'border-gray-300 hover:border-gray-400'
        )}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setTimeout(() => inputRef.current?.focus(), 50);
        }}
      >
        {value === 'custom' ? (
          <span className="flex-1 text-sm text-gray-700">{t('teltonika_picker.custom')}</span>
        ) : selectedCmd ? (
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-gray-900 truncate">{selectedCmd.label}</span>
            <span className={clsx('text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap', DEVICE_BADGE_COLORS[selectedCmd.devices] || 'bg-gray-100 text-gray-600')}>
              {selectedCmd.devices}
            </span>
          </div>
        ) : (
          <span className="flex-1 text-sm text-gray-400">{t('teltonika_picker.placeholder')}</span>
        )}
        {value && value !== '' ? (
          <X className="h-4 w-4 text-gray-400 hover:text-gray-600 shrink-0" onClick={(e) => { e.stopPropagation(); handleClear(); }} />
        ) : (
          <ChevronDown className={clsx('h-4 w-4 text-gray-400 shrink-0 transition-transform', isOpen && 'rotate-180')} />
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-[420px] flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white rounded-t-lg">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('teltonika_picker.search_placeholder')}
                className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30"
              />
            </div>
          </div>

          {/* Command list */}
          <div ref={listRef} className="overflow-y-auto flex-1">
            {Object.keys(grouped).length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {t('teltonika_picker.no_match', { query: search })}
              </div>
            ) : (
              Object.entries(grouped).map(([cat, cmds]) => (
                <div key={cat}>
                  <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0">
                    {cat}
                  </div>
                  {cmds.map((cmd) => (
                    <button
                      key={cmd.value}
                      type="button"
                      className={clsx(
                        'w-full text-left px-3 py-2 hover:bg-cyan-50 transition-colors flex items-start gap-2',
                        cmd.value === value && 'bg-cyan-50'
                      )}
                      onClick={() => handleSelect(cmd)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{cmd.label}</span>
                          {cmd.needsValue && (
                            <span className="text-[10px] text-amber-600 bg-amber-50 px-1 rounded">{t('teltonika_picker.value_required')}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{cmd.description}</p>
                        {cmd.valueHint && (
                          <p className="text-[10px] text-gray-400 mt-0.5">{t('teltonika_picker.range_label', { hint: cmd.valueHint })}</p>
                        )}
                      </div>
                      <span className={clsx('text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap mt-0.5', DEVICE_BADGE_COLORS[cmd.devices] || 'bg-gray-100 text-gray-600')}>
                        {cmd.devices}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}

            {/* Custom option always at end */}
            <div className="border-t border-gray-100">
              <button
                type="button"
                className={clsx(
                  'w-full text-left px-3 py-2.5 hover:bg-cyan-50 transition-colors',
                  value === 'custom' && 'bg-cyan-50'
                )}
                onClick={handleCustom}
              >
                <span className="text-sm font-medium text-gray-700">{t('teltonika_picker.custom_option')}</span>
                <p className="text-xs text-gray-400 mt-0.5">{t('teltonika_picker.custom_help')}</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
